/**
 * Lead Capture Popup — vanilla JS, no dependencies.
 * Backend: Supabase REST API (anon key, INSERT-only via RLS — see supabase-schema.sql).
 * Triggers: 15s dwell time OR N page views (whichever fires first).
 * Frequency cap: once per session, via sessionStorage.
 * Name is optional — only email is required to receive the eBook.
 */
(function () {
  'use strict';

  var STORAGE_SHOWN = 'lcp_shown_this_session';
  var STORAGE_PAGEVIEWS = 'lcp_pageview_count';
  var STORAGE_UTM = 'lcp_utm_first_touch';
  var STORAGE_SUBMITTED_EMAIL = 'lcp_submitted_email';

  var root = document.querySelector('[data-lcp]');
  if (!root) return;

  var alreadyShown = sessionStorage.getItem(STORAGE_SHOWN) === 'true';
  if (alreadyShown) return;

  (function captureUtmOnce() {
    if (sessionStorage.getItem(STORAGE_UTM)) return;
    var params = new URLSearchParams(window.location.search);
    var utm = {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null
    };
    sessionStorage.setItem(STORAGE_UTM, JSON.stringify(utm));
  })();

  var pageViews = parseInt(sessionStorage.getItem(STORAGE_PAGEVIEWS) || '0', 10) + 1;
  sessionStorage.setItem(STORAGE_PAGEVIEWS, String(pageViews));

  var pageviewThreshold = parseInt(root.getAttribute('data-pageview-threshold') || '2', 10);
  var delaySeconds = parseInt(root.getAttribute('data-delay') || '15', 10);

  var timerId = null;
  var opened = false;

  function markShownAndOpen() {
    if (opened) return;
    opened = true;
    if (timerId) clearTimeout(timerId);
    openPopup();
  }

  if (pageViews >= pageviewThreshold) {
    markShownAndOpen();
  } else {
    timerId = setTimeout(markShownAndOpen, delaySeconds * 1000);
  }

  var lastFocusedEl = null;
  var focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function getFocusable() {
    return Array.prototype.slice.call(root.querySelectorAll(focusableSelector)).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function openPopup() {
    lastFocusedEl = document.activeElement;
    root.hidden = false;
    document.body.style.overflow = 'hidden';

    var focusables = getFocusable();
    if (focusables.length) focusables[0].focus();

    document.addEventListener('keydown', onKeydown, true);
  }

  function closePopup(persist) {
    root.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown, true);

    if (persist !== false) {
      sessionStorage.setItem(STORAGE_SHOWN, 'true');
    }
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
      lastFocusedEl.focus();
    }
  }

  function onKeydown(e) {
    if (root.hidden) return;

    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closePopup();
      return;
    }

    if (e.key === 'Tab') {
      var focusables = getFocusable();
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  Array.prototype.forEach.call(root.querySelectorAll('[data-lcp-close]'), function (el) {
    el.addEventListener('click', function () {
      closePopup();
    });
  });

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function setFieldError(input, errorEl, message) {
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      errorEl.textContent = message;
    } else {
      input.setAttribute('aria-invalid', 'false');
      errorEl.textContent = '';
    }
  }

  // Name is optional: only trimmed and passed through, never blocks submission.
  // Email is the sole required field.
  function validateForm(form) {
    var nameInput = form.querySelector('#lcp-name');
    var emailInput = form.querySelector('#lcp-email');
    var emailError = form.querySelector('#lcp-email-error');

    var isValid = true;

    var nameVal = nameInput.value.trim();

    var emailVal = emailInput.value.trim();
    if (!EMAIL_RE.test(emailVal)) {
      setFieldError(emailInput, emailError, 'Please enter a valid email address.');
      isValid = false;
    } else {
      setFieldError(emailInput, emailError, '');
    }

    if (!isValid) {
      emailInput.focus();
      return null;
    }

    return { name: nameVal, email: emailVal };
  }

  function buildPayload(name, email) {
    var utm = {};
    try {
      utm = JSON.parse(sessionStorage.getItem(STORAGE_UTM) || '{}');
    } catch (e) {
      utm = {};
    }

    return {
      full_name: name || null,
      email: email,
      source_url: window.location.href,
      referrer: document.referrer || null,
      utm_source: utm.utm_source || null,
      utm_medium: utm.utm_medium || null,
      utm_campaign: utm.utm_campaign || null
    };
  }

  var form = root.querySelector('[data-lcp-form]');
  var isSubmitting = false;

  if (form) {
    var previouslySubmittedEmail = sessionStorage.getItem(STORAGE_SUBMITTED_EMAIL);
    if (previouslySubmittedEmail) {
      showSuccessStep();
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      if (isSubmitting) return;

      var honeypot = form.querySelector('#lcp-company');
      if (honeypot && honeypot.value.trim() !== '') return;

      var formErrorEl = form.querySelector('[data-lcp-form-error]');
      formErrorEl.hidden = true;
      formErrorEl.textContent = '';

      var values = validateForm(form);
      if (!values) return;

      var supabaseUrl = root.getAttribute('data-supabase-url');
      var anonKey = root.getAttribute('data-supabase-anon-key');

      if (!supabaseUrl || !anonKey) {
        formErrorEl.textContent = 'This form is not fully configured yet. Please try again later.';
        formErrorEl.hidden = false;
        return;
      }

      var submitBtn = form.querySelector('[data-lcp-submit]');
      isSubmitting = true;
      submitBtn.disabled = true;
      submitBtn.setAttribute('data-loading', 'true');

      var endpoint = supabaseUrl.replace(/\/$/, '') + '/rest/v1/leads';
      var payload = buildPayload(values.name, values.email);

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: anonKey,
          Authorization: 'Bearer ' + anonKey,
          Prefer: 'return=minimal'
        },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          if (response.status === 201 || response.status === 204) {
            sessionStorage.setItem(STORAGE_SHOWN, 'true');
            sessionStorage.setItem(STORAGE_SUBMITTED_EMAIL, values.email);
            showSuccessStep();
            return;
          }

          return response.json().catch(function () {
            return {};
          }).then(function (body) {
            throw { status: response.status, body: body };
          });
        })
        .catch(function (err) {
          handleSubmitError(err, formErrorEl);
        })
        .finally(function () {
          isSubmitting = false;
          submitBtn.disabled = false;
          submitBtn.removeAttribute('data-loading');
        });
    });
  }

  function handleSubmitError(err, formErrorEl) {
    if (err && err.body && err.body.code === '23505') {
      formErrorEl.textContent = "Looks like you're already on the list — check your inbox for the guide.";
      formErrorEl.hidden = false;
      return;
    }

    if (err && err.status === 400 && err.body && /too many/i.test(err.body.message || '')) {
      formErrorEl.textContent = err.body.message;
      formErrorEl.hidden = false;
      return;
    }

    formErrorEl.textContent =
      'Something went wrong sending your request. Please check your connection and try again.';
    formErrorEl.hidden = false;
  }

  function showSuccessStep() {
    var formStep = root.querySelector('[data-lcp-step="form"]');
    var successStep = root.querySelector('[data-lcp-step="success"]');
    if (formStep) formStep.hidden = true;
    if (successStep) {
      successStep.hidden = false;
      var heading = successStep.querySelector('.lcp__heading');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }
  }
})();
